import { useState, useEffect, useContext } from 'react';
import {
  Link,
  useParams,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from 'react-router-dom';
import { Button } from '@nike/eds';
import {
  ArrowLeft,
  Search,
  NikeSwoosh,
  CaretLeft,
  CaretRight,
} from '@nike/nike-design-system-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import classnames from 'classnames';

import { AllProductContext } from './product';
import ToggleTerm from 'components/toggleTerm';
import Loader from 'components/Loader';

import { QUERY_KEYS } from '../../constants';
import labellinClientAPI, {
  Product_SearchTerm,
} from '../../clientAPI/labelling';
import propagate403Error from '../../utils/propagate403Error';
import { isExistScope } from '../../utils/authHelpers';

export default function LabellingProductItem() {
  const queryClient = useQueryClient();
  const { datasetId, productId } = useParams();
  const loaderData = useLoaderData() as { access_token: string };
  const jwt = loaderData.access_token;
  const canWrite = isExistScope('search360:labeling:write', jwt);
  const revalidator = useRevalidator();
  const allProductInfo = useContext(AllProductContext);
  const navigate = useNavigate();

  const [mySearchTerms, setMySearchTerms] = useState<
    Array<{ searchTerm: string; recall: boolean }>
  >([]);
  const [searchInput, setSearchInput] = useState('');

  const { status, data, error } = useQuery({
    queryKey: [
      QUERY_KEYS.LABELLING_DATASET_PRODUCT_DETAIL,
      datasetId,
      productId,
    ],
    queryFn: async () =>
      labellinClientAPI.findDatasetProductById(
        datasetId!,
        productId!,
        loaderData.access_token,
      ),
    staleTime: Infinity,
    onError: (err: { status: number }) =>
      err.status === 401 && revalidator.revalidate(),
  });

  const mutation = useMutation({
    mutationFn: (searchTerms: Product_SearchTerm[]) => {
      return labellinClientAPI.labelDatasetProduct(
        datasetId!,
        productId!,
        searchTerms,
        loaderData.access_token,
      );
    },
    onError: (err: { status: number }) =>
      err.status === 401 && revalidator.revalidate(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.LABELLING_DATASET_PRODUCT_DETAIL,
          datasetId,
          productId,
        ],
      });
      allProductInfo.increaseLabelledAmount();
      navigate(
        `/labelling/datasets/${datasetId}/products/${allProductInfo.nextProductId}`,
      );
      document
        .getElementById('layout-content')
        ?.scroll({ top: 0, behavior: 'smooth' });
    },
  });

  useEffect(() => {
    if (data) {
      setMySearchTerms(data.searchTerms);
    }
    setSearchInput('');
  }, [data, productId]);

  const toggleTermState = (term: string) => {
    if (!mySearchTerms) return;
    const newMySearchTerms = [...mySearchTerms];
    const idx = newMySearchTerms.findIndex((item) => item.searchTerm === term);
    newMySearchTerms[idx].recall = !newMySearchTerms[idx].recall;
    setMySearchTerms(newMySearchTerms);
  };

  const onSearchInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value.trim();
    setSearchInput(value);
  };

  const submit = () => {
    mutation.mutate(mySearchTerms);
  };

  const backToDatasetList = () => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.LABELLING_DATASET_PRODUCTS, datasetId],
    });
    navigate('/labelling/datasets');
  };

  const selectedTerms = mySearchTerms
    ? mySearchTerms.filter((term) => term.recall)
    : [];

  propagate403Error(error as keyable);
  if (status === 'loading') return <Loader />;
  if (!data)
    return (
      <div className='tw-w-full tw-h-2/3 tw-flex tw-items-center tw-justify-center'>
        <h2 className='eds-type--display-4'>Product data unavailable</h2>
      </div>
    );

  return (
    <div className='tw-mx-auto tw-py-10 tw-w-10/12'>
      <div className='tw-flex tw-justify-between'>
        <Button
          size='small'
          variant='ghost'
          beforeSlot={<ArrowLeft />}
          onClick={backToDatasetList}
        >
          Dataset List
        </Button>

        <div className='tw-flex tw-items-center'>
          {allProductInfo.isCurrentLabelled && (
            <span className='tw-rounded-full tw-block tw-px-6 tw-py-1 tw-mr-10 tw-text-emerald-100 tw-bg-emerald-500'>
              Labelled
            </span>
          )}
          <p className='tw-mr-10'>{`Submitted: ${allProductInfo.labelledProductAmount} / ${allProductInfo.totalProductAmount}`}</p>
          <Link
            to={`/labelling/datasets/${datasetId}/products/${allProductInfo.prevProductId}`}
            className='tw-transition-all hover:tw-opacity-50 tw-pr-4 tw-cursor-pointer tw-flex tw-items-center'
          >
            <CaretLeft /> Prev
          </Link>
          <Link
            to={`/labelling/datasets/${datasetId}/products/${allProductInfo.nextProductId}`}
            className='tw-transition-all hover:tw-opacity-50 tw-pl-4 tw-cursor-pointer tw-flex tw-items-center'
          >
            Next <CaretRight />
          </Link>
        </div>
      </div>

      <div className='tw-flex tw-mt-8 tw-justify-between'>
        <div className='tw-bg-white tw-rounded-t-lg tw-p-8 tw-flex-1 tw-mr-10 tw-self-start'>
          <p className='eds-type--display-5 tw-text-blue-400'>Product Info</p>
          <p className='tw-font-bold tw-my-4'>{data.fullTitle}</p>
          <div className='tw-w-full tw-flex tw-flex-wrap tw-gap-4'>
            {data.images.map((img, idx) => (
              <img
                alt='product'
                key={idx}
                src={img}
                className='tw-object-contain tw-w-32 tw-h-32 tw-rounded'
              />
            ))}
          </div>
          <div
            className='tw-text-sm tw-mt-8'
            dangerouslySetInnerHTML={{
              __html: data.htmlDescription,
            }}
          />
        </div>

        <div className='tw-bg-white tw-rounded-lg tw-p-8 tw-flex-1'>
          <p className='eds-type--display-5 tw-mb-2 tw-text-red-400'>
            Search Terms
          </p>

          <p className='tw-text-sm tw-text-gray-700'>
            Please select the terms that matching the product
          </p>

          <div className='tw-flex tw-items-center tw-rounded-full tw-border tw-px-6 tw-py-1 tw-mt-4 tw-mb-8'>
            <Search className='tw-text-sm tw-text-gray-400 tw-mr-2' />
            <input
              className='tw-border-0 text-sm tw-p-1 tw-outline-0 tw-flex-1'
              placeholder='Find search terms...'
              value={searchInput}
              onChange={onSearchInputChange}
            />
          </div>

          <div className='tw-flex tw-flex-wrap tw-justify-center'>
            {mySearchTerms &&
              mySearchTerms
                .filter(
                  (term) =>
                    searchInput.length === 0 ||
                    term.searchTerm.includes(searchInput),
                )
                .map((term) => (
                  <ToggleTerm
                    key={term.searchTerm}
                    className='tw-mx-3 tw-mb-5'
                    title={term.searchTerm}
                    isSelected={term.recall}
                    onClick={toggleTermState.bind(null, term.searchTerm)}
                  />
                ))}
          </div>
        </div>
      </div>

      <div className='tw-flex tw-mt-10 tw-mb-32'>
        <div className='tw-flex-1'>
          {selectedTerms.length > 0 && (
            <>
              <p className='tw-text-sm tw-font-bold tw-mb-2'>My selections:</p>
              <div className='tw-flex tw-flex-wrap'>
                {selectedTerms.map((term) => (
                  <span
                    key={term.searchTerm}
                    className='tw-rounded-full tw-bg-black tw-text-white tw-font-bold tw-text-sm tw-mr-3 tw-mb-2 tw-py-1 tw-px-3'
                  >
                    {term.searchTerm}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        {canWrite && (
          <Button
            className='tw-self-start tw-w-48 tw-ml-36'
            beforeSlot={
              <NikeSwoosh
                className={classnames({
                  'tw-animate-pulse': mutation.isLoading,
                })}
              />
            }
            size='medium'
            variant='primary'
            onClick={submit}
          >
            {(mutation.isLoading && 'Submitting...') || 'Submit'}
          </Button>
        )}
      </div>
    </div>
  );
}
